import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { TileArtwork } from "@/components/grow/TileArtwork";

// The head of a page below Grow · Play: back, eyebrow, title, description, and
// the tile artwork on the right. Shared by the questionnaire group page, the
// game group page and the game page, so the three read as one family.
export function PlayPageHeader({
  title,
  titleId,
  eyebrow,
  description,
  artwork,
  backTo,
  hideDescriptionWhenNarrow = false,
}: {
  // Absent while the subject is unknown or still loading.
  title?: string;
  // Test anchor for the heading.
  titleId?: string;
  eyebrow?: ReactNode;
  description?: string;
  artwork?: { hue: number; imageSrc?: string };
  // A fixed parent instead of the previous history entry (see below).
  backTo?: string;
  hideDescriptionWhenNarrow?: boolean;
}) {
  const intl = useIntl();
  const backLabel = intl.formatMessage({ id: "common.back", defaultMessage: "Back" });
  return (
    <>
      <div className="mb-5">
        {backTo ? (
          // The one documented exception to BackButton's navigate(-1)
          // (UI_CONVENTIONS, "Circular back"): a game is also opened from the
          // profile, and its way back is still the group it belongs to.
          <Link id="back-button" to={backTo} title={backLabel} aria-label={backLabel} className="smycles-back-button">
            ←
          </Link>
        ) : (
          <BackButton />
        )}
      </div>
      <header className="mb-8 flex items-start justify-between gap-6">
        <div>
          <p className="text-smycles-meta tracking-wide text-ink-muted">
            {eyebrow ?? <FormattedMessage id="grow.play.quiz.eyebrow" defaultMessage="Grow · Play" />}
          </p>
          {title ? (
            <h1 id={titleId} className="smycles-page-title mt-1">
              {title}
            </h1>
          ) : null}
          {description ? (
            <p className={`mt-2 text-smycles-body text-ink-muted ${hideDescriptionWhenNarrow ? "hidden @sm:block" : ""}`}>
              {description}
            </p>
          ) : null}
        </div>
        {artwork ? (
          // Without artwork the subject keeps its identity through the same
          // id-derived hue its catalogue tile falls back to.
          <TileArtwork
            imageSrc={artwork.imageSrc}
            hue={artwork.hue}
            variant="grid"
            className="hidden aspect-[6/5] w-56 shrink-0 rounded-xl ring-1 ring-ink/10 @sm:block"
          />
        ) : null}
      </header>
    </>
  );
}
